import * as React from 'react';
import { useEffect, useState } from 'react';
import { Alert, Card, CardBody, CardText, CardTitle, Col, Input, Label, Row, Table, Button } from 'reactstrap';
import Swal from 'sweetalert2';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import {
    Afr, Audit, AuditMessage, clearTableFactRows, generateLocalStateFromEventProperties,
    getSelectedAfr, getTableTotals, isAFRAuditRequired, updateAfrAudit, updateVersionStateValue, hasDiscreteAffiliates, isFundGroupOriented
} from '../../redux/AfrSlice';
import { Auditor, loadAuditor, resetAuditorState, selectAuditor } from '../../redux/AuditorSlice';
import { getAllAuditorsAsync, selectAuditors } from '../../redux/AuditorsSlice';
import { DocumentRelationship } from '../../redux/DocumentsSlice';
import { Entity, getSelectedEntity } from '../../redux/EntitySlice';
import { isAdmin, setIsDirty } from '../../redux/MiscSlice';
import { AFRError } from '../../redux/ValidationSlice';
import enums from '../../utils/enums';
import utilityHelper from '../../utils/utilityHelper';
import AuditEditor from '../feature/AuditEditor';
import Page from '../Page';
import DataElementWorksheetCore from './DataElementWorksheetCore';
import DataElementWorksheetCalculated from './DataElementWorksheetCalculated';
import { AdminUserRoleEnum, selectAdminUserRole } from '../../redux/UserSlice';
import { TaxonomyTypeEnum } from '../../redux/TaxonomyVersionSlice';
import { loadConcept, resetConceptState } from '../../redux/ConceptSlice';




const AuditCore = (props: { userRole: string }) => {
    const dispatch = useAppDispatch();

    const [activeTab, setActiveTab] = useState("1");
    const isAdminUser: boolean = useAppSelector(isAdmin);
    const adminUserRole: AdminUserRoleEnum | null = useAppSelector(selectAdminUserRole);
    const auditorsList = useAppSelector(selectAuditors);
    const currentAuditor: Auditor | null = useAppSelector(selectAuditor);
    const auditMessage: AuditMessage = useAppSelector(isAFRAuditRequired);
    var pdf : DocumentRelationship | null = null;
    var errorMessage: Array<AFRError> = [];

    const currentAfr: Afr | null = useAppSelector(getSelectedAfr);
    var ad: Audit | null = null;
    var auditType: any = "NONE";
    if (currentAfr && currentAfr.currentversion && currentAfr.currentversion.audit) {
        ad = currentAfr.currentversion.audit;
        auditType = ad.auditType;
        pdf = ad.auditPdf;
    }
    const [state, setState] = useState(generateLocalStateFromEventProperties(currentAfr, ["reviewState_auditReviewed"]));
    const [hasAudit, setHasAudit] = useState(ad ? true : false);
    const [audit, setAudit] = useState(auditType);

    const afrYear = currentAfr ? currentAfr.year : 0;
    const entity: Entity | null = useAppSelector(getSelectedEntity);
    const entityName = entity ? entity.name : "";
    const entityCode = entity ? entity.code : "";
    /*
        DEW_910001_AssignedFundBalance
        DEW_910002_UnassignedFundBalance
        DEW_910003_UnassignedAndAssignedFundBalance
        DEW_910004_RestrictedNetPosition
        DEW_910005_CashAndInvestments
        DEW_910006_CurrentLiabilities
        DEW_910007_LongTermDebt
        DEW_920001_TotalRevenues
        DEW_920002_IntergovernmentalRevenues
        DEW_920003_TotalOperatingRevenues
        DEW_930001_TotalExpenditures
        DEW_930002_DebtServiceExpenditures
        DEW_930003_TotalOperatingExpenses
    */

    const linkStyle : any = {
        color: "blue",
        textDecoration: "underline"
    };
    const defaultDewState = [
        {
            name: "Balance Sheet Items",
            valueGroups: [
                [
                    {
                        name: "Cash and Investments",
                        code: "910005",
                        fundGroup: "Governmental",
                        accountSource: [{ min: "101.000", max: "104.000", fund: "Governmental" }, { min: "151.000", max: "151.900", fund: "Governmental" }],
                        value: 0
                    },
                    {
                        name: "Cash and Investments",
                        code: "910005",
                        fundGroup: "Proprietary",
                        accountSource: [{ min: "101.000", max: "104.000", fund: "Proprietary" }, { min: "151.000", max: "151.900", fund: "Proprietary" }],
                        value: 0
                    }

                ],
                [
                    {
                        name: "Current Liabilities",
                        code: "910006",
                        fundGroup: "Governmental",
                        accountSource: [{ min: "201.000", max: "235.000", fund: "Governmental", subFilter: ".000" }],
                        value: 0
                    },
                    {
                        name: "Current Liabilities",
                        code: "910006",
                        fundGroup: "Proprietary",
                        accountSource: [{ min: "201.000", max: "235.000", fund: "Proprietary", subFilter: ".000" }],
                        value: 0
                    }
                ],
                [
                    {
                        name: "Assigned Fund Balance",
                        code: "910001",
                        fundGroup: "Governmental",
                        accountSource: [{ min: "283.000", max: "283.000", fund: "Governmental" }],
                        value: 0
                    },
                    {
                        name: "Unassigned Fund Balance",
                        code: "910002",
                        fundGroup: "Governmental",
                        accountSource: [{ min: "284.000", max: "284.000", fund: "Governmental" }],
                        value: 0
                    },
                    {
                        name: "Restricted Net Position",
                        code: "910004",
                        fundGroup: "Proprietary",
                        accountSource: [{ min: "275.000", max: "275.000", fund: "Proprietary" }],
                        value: 0
                    }
                ]
            ]
        }, {
            name: "Revenues",
            valueGroups: [
                [
                    {
                        name: "Total Revenues",
                        code: "920001",
                        fundGroup: "Governmental",
                        accountSource: [{ min: "311.000", max: "329.500", fund: "Governmental" }, { min: "341.100", max: "393.000", fund: "Governmental" }],
                        value: 0
                    },
                    {
                        name: "Intergovernmental Revenues",
                        code: "920002",
                        fundGroup: "Governmental",
                        accountSource: [{ min: "331.100", max: "339.000", fund: "Governmental" }],
                        value: 0
                    },
                    {
                        name: "Intergovernmental Revenues",
                        code: "920002",
                        fundGroup: "Proprietary",
                        accountSource: [{ min: "331.100", max: "339.000", fund: "Proprietary" }],
                        value: 0
                    },
                    {
                        name: "Total Operating Revenues",
                        code: "920003",
                        fundGroup: "Proprietary",
                        accountSource: [{ min: "341.100", max: "349.000", fund: "Proprietary" }],
                        value: 0
                    }

                ]
            ]

        }, {
            name: "Expenditures",
            valueGroups: [
                [
                    {
                        name: "Total Expenditures",
                        code: "930001",
                        fundGroup: "Governmental",
                        accountSource: [{ min: "511", max: "516", fund: "Governmental" }, { min: "519", max: "769", fund: "Governmental" }],
                        value: 0
                    },
                    {
                        name: "Debt Service Expenditures",
                        code: "930002",
                        fundGroup: "Governmental",
                        accountSource: [{ min: "517", max: "518", fund: "Governmental" }],
                        value: 0
                    },
                    {
                        name: "Total Operating Expenses",
                        code: "930003",
                        fundGroup: "Proprietary",
                        accountSource: [{ min: "513", max: "513", fund: "Proprietary" }, { min: "531", max: "579", fund: "Proprietary" }],
                        value: 0
                    }

                ]
            ]
        }, {
            name: "Long Term Debt",
            valueGroups: [
                [
                    {
                        name: "Long Term Debt",
                        code: "910007",
                        fundGroup: "Governmental",
                        accountSource: [{ min: "203.900", max: "239.900", fund: "Governmental", subFilter: ".900" }],
                        value: 0
                    }
                ]
            ]
        }
    ];

    const getUpdatedValueGroup = function (dewAccounts: any[],calcMap : any): any[] {
        var results: any[] = [];
        dewAccounts.map((dewAccount: any) => {
            var accKey = dewAccount.name + "_" + dewAccount.fundGroup;
            if (calcMap.hasOwnProperty(accKey)) {
                results.push(calcMap[accKey]);
            } else {
                results.push(dewAccount);
            }
        });
        return results;
    }

    const getUpdatedValueCategory = function (vg: any, calcMap : any): any {
        var updatedValueGroups: any[] = [];
        vg.valueGroups.map((vsource: any) => updatedValueGroups.push(getUpdatedValueGroup(vsource, calcMap)))
        var newValueCategory: any = {
            name: vg.name,
            valueGroups: updatedValueGroups
        };
        return newValueCategory;
    }

    const getUpdatedDewMap = function (defMapList: any): any {
        if (!currentAfr || !currentAfr.calculations || currentAfr.calculations.length === 0) {
            return defMapList;
        }
        if (currentAfr.calculations === "{}")
        {
            return defMapList;
        }
        var calcMap: any = {};
        JSON.parse(currentAfr.calculations).map((dewValue: any) => calcMap[dewValue.name + "_" + dewValue.fundGroup] = dewValue);
        var updatedMap: any = [];
        defMapList.map((vg: any) => updatedMap.push(getUpdatedValueCategory(vg, calcMap)));
        return updatedMap;
    }

    const [dewState, setDewState] = useState<any[]>(getUpdatedDewMap(defaultDewState));

    const getAccountRange = function (dr: any): string {
        var result = "";
        dr.accountSource.map((range: any) => {
            if (result.length > 0) {
                result += ", ";
            }
            if (range.min === range.max) {
                result += range.min;
            } else {
                result += range.min + " - " + range.max;
            }
            if (range.hasOwnProperty("subFilter")) {
                result += " ( " + range.subFilter + " only)";
            }
        });
        return result;
    }

    const loadDewAccount = (accountId?: string) => {
        if (accountId && accountId.length > 0) {
            dispatch(loadConcept({
                conceptId: "AccountCode_" + accountId,
                taxonomyType: currentAfr ? currentAfr.taxonomyType : TaxonomyTypeEnum.AFR,
                afrYear: afrYear
            }));
        } else {
            dispatch(resetConceptState({}));
        }
    }

    const collectValueMappings = function (ds: any): any[] {
        var result: any[] = [];
        var visited: any = {};
        ds.map((section: any) => {
            section.valueGroups.map((vg: any) => {
                vg.map((v: any) => {
                    if (!visited.hasOwnProperty(v.name)) {
                        result.push(v);
                        visited[v.name] = true;
                    }
                });
            });
        });
        return result;

    }
    const handleChangeCheckbox = function (event : any) {

        var checkValues = JSON.parse(JSON.stringify(state));
        var key: string = event.target.name;
        var value: boolean = event.target.checked
        checkValues[key] = value;

        setState(checkValues);

        // here call to dispatcher sending:
        var eventFirstlevel = key.split("_")[0]
        var eventSecondlevel = key.split("_")[1]

        // Update global flag for this event in redux
        dispatch(updateVersionStateValue({ propGroup: eventFirstlevel, propName: eventSecondlevel, boolValue: value, value: null }));

        // set the dirty flag to TRUE
        dispatch(setIsDirty({ IsDirty: true, Page: "" }))
    }



    const resetAudit = function (auditType: string /*DEW or AUDIT */) {
        /* remove audit object */
        var audit: Audit | null = null;

        audit =
        {
            auditType: auditType,
            auditorReference: null,
            auditPdf: null,
            datePerformed: null,
            sendAuditPdf: null,
            dateReceived: null,
        };

        // Update in redux
        dispatch(updateAfrAudit(audit));
    }

    const createAuditOption = function (option: string) {
        var audit : Audit =
        {
            auditType: option,
            auditorReference: null,
            auditPdf: null,
            datePerformed: null,
            sendAuditPdf: null,
            dateReceived: null,
        };

        // Update in redux
        dispatch(updateAfrAudit(audit));
        if (currentAfr && currentAfr.year < 2020 && option === "AUDIT") {
            // We must clear out any DEW facts
            dispatch(clearTableFactRows("uas_063_data-element-worksheet"));
        }

        // Turn ON the dirty flag in redux
        dispatch(setIsDirty({ IsDirty: true, Page: "" }));

        setHasAudit((option === "AUDIT") && (ad ? true : false));
        setAudit(option);
    }
    const handleChange = function (event: any) {
        var value: string = event.target.value
        if (ad) {
            if (ad.auditType === value) {
                return;
            }
            proceedAnswer(value);
        } else {
            createAuditOption(value);
        }
        setAudit(value);
    }

    
    const isAuditDirty = function (audit: Audit | null) {
        if (audit) {
            if (audit.auditorReference || audit.auditPdf || (audit.datePerformed && audit.datePerformed.length > 0) || audit.sendAuditPdf || audit.dateReceived) {
                if (audit.auditType === "DEW") { return false;}
                return true;
            }
        }
        return false;
    }

    const isDEWDirty = function () {
        if (!(currentAfr)) { return false;}
        return (getTableTotals(currentAfr, enums.tableName.dew) > 0);
    }


    const proceedAnswer = function (option: string) {
        var htmlText: string;
        var textResponse: string;

        if (isFundGroupOriented(currentAfr) && option === "AUDIT") {
            // Update in redux
            createAuditOption(option);
            return;
        }
        htmlText = 'Please take into account that your current DEW information will be lost if you select YES.<br/><br/>Do you want to proceed?';

        textResponse = 'Your DEW information has been deleted.';

        if (option === 'DEW') {
            htmlText = 'Please take into account that your current <b>Audit</b> information will be lost ' +
                'if you select NO.<br/><br/>' +
                'Do you want to proceed? ';
            textResponse = 'Your Audit information has been deleted.';
        }

        Swal.fire({
            title: 'Are you sure?',
            html: htmlText,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result: any) => {
            if (result.isConfirmed) {


                // Update in redux
                createAuditOption(option);

                Swal.fire(
                    'Deleted!',
                    textResponse,
                    'success'
                )
            }
            else {
                setAudit('DEW');
                if (option === 'DEW') {
                    setAudit('AUDIT');
                }
            }
        });

    }
    const shouldShowAudit = function (currentAfr: Afr | null, entity: Entity | null): boolean {
        var result: boolean = false;
        if (!currentAfr || !currentAfr.currentversion) {
            return false;
        }
        if (!entity) {
            return false;
        }
        if (isFundGroupOriented(currentAfr) && !currentAfr.currentversion.audit) {
             return false;
        }
        if (utilityHelper.isDonePrimaryDataEntry(currentAfr, entity)) {
            return true;
        }
        if (currentAfr.currentversion.audit && currentAfr.currentversion.audit.auditPdf) {
            return true;
        }
        return result;
    }
    const auditIsNotRequiredMessage = function (afr : Afr | null) {

        var msg = afr && isFundGroupOriented(afr)
            ?
            "If an audit was performed, it can be reported. Otherwise, a Data Element Worksheet will be generated."
            :
            "You must report a Data Element Worksheet (DEW) or if you have an audit done, then it can be reported instead.";
        return (
        <div>
            <Alert color="warning">
                    <p>This AFR does not require an audit per 218.39 (1)(i), F.S.</p>
                    {auditMessage.message.length > 0 && (<p>{auditMessage.message}</p>)}
                    {utilityHelper.isDonePrimaryDataEntry(currentAfr, entity) && (<p>{msg}</p>)}
            </Alert>
        </div>
            )
    }
    const canShowAuditTypeSelector = function (afr: Afr | null): boolean {
        if (!afr) {
            return true;
        }
        
        if (auditMessage.requireAudit) {
            if (!utilityHelper.isDonePrimaryDataEntry(afr, entity)) {
                // We prevent answering audit questions (even if required) until data entry is complete
                return false;
            }
            if (!afr.currentversion.audit) {
                return true;
            }
            if (afr.currentversion.audit.auditType == "DEW") {
                // Alow type change if we have a DEW
                // e.g.because we previously didn't require an audit, and said NO, but now do require one after exceeding a threshold
                return true;
            }
            return true;
        }
        return utilityHelper.isDonePrimaryDataEntry(afr, entity)
    }
    const handleSaveAudit = function (audit: Audit) {

        var updatedAudit: Audit = { ...audit };

        // Update in redux
        dispatch(updateAfrAudit(updatedAudit));
    }

    const getAnnotatedValue = function (value: string): string {
        if (afrYear >= 2024 && value === "DEW") {
            return value + "_CALCULATED";
        }
        return value;
    }

    useEffect(() => {
        // get state from redux
        var newLocalState = generateLocalStateFromEventProperties(currentAfr, ["reviewState_auditReviewed"])

        // create a local state from redux
        setState(newLocalState)
        if (currentAfr &&
            currentAfr.currentversion &&
            currentAfr.currentversion.audit &&
            currentAfr.currentversion.audit.auditorReference
        ) {
            dispatch(loadAuditor(currentAfr.currentversion.audit.auditorReference.code));
        } else {
            if (!isFundGroupOriented(currentAfr)) {
                console.log("dont do this");
                dispatch(resetAuditorState({}));
            }
        }

    }, [currentAfr]);

    React.useEffect(() => {
        if (!auditorsList || auditorsList.length === 0) {
            dispatch(getAllAuditorsAsync());
        }
        // save all the pending changes into AFRSlice
        utilityHelper.saveDeferredChanges(dispatch);
        setDewState(getUpdatedDewMap(defaultDewState));
    }, []);


    useEffect(() => {
        if (isFundGroupOriented(currentAfr)) {
            if (!ad) {
                setAudit("NONE");
            } else {
                setAudit(ad.auditType);
            }
        } else {
            var auditInFile = (isAuditDirty(ad) || isDEWDirty());
            var auditType = "AUDIT";

            if (auditMessage.requireAudit) {
                setAudit("AUDIT");
                if (isDEWDirty()) {
                    // We must clear out any DEW facts if exist any
                    dispatch(clearTableFactRows(enums.tableName.dew));
                }
                if (!isAuditDirty(ad)) {
                    resetAudit(auditType);
                }
            } else {
                if (!auditInFile || isDEWDirty()) {
                    auditType = "DEW";
                    resetAudit(auditType);
                }

                setAudit(auditType);
            }
        }

    }, [currentAuditor]);


    const switchContent = (value: string) => {

        switch (getAnnotatedValue(value)) {
            case 'AUDIT':
                return <Row>
                    <Col>
                        <Card body>
                            <CardTitle>Auditor information</CardTitle>
                            {currentAfr && currentAfr.currentversion
                                ? <CardBody>
                                    
                                    <AuditEditor containerParentOfAudit={currentAfr.currentversion} containerType={enums.auditSource.AFRCurrentVersion } saveAudit={handleSaveAudit}  />

                                </CardBody>
                                : <CardText>No auditor</CardText>
                            }

                        </Card>
                    </Col>
                </Row>;
            case 'DEW':
                return <Row>
                    <Col style={{width:"50%"}}>
                        <DataElementWorksheetCore userRole="ADMIN" />
                    </Col>
                    <Col>
                        <h4>All fields must be complete in order to submit. Any whole number is valid, including zero and negatives.</h4>
                        <h4><p style={{ marginBottom: "0" }}>Please direct questions regarding the information required in this page to the Auditor General’s office:</p><p style={{ marginTop: "0", paddingLeft: "3rem" }}>Phone: (850) 487-9301<br />Email: flaudgen_localgovt@aud.state.fl.us</p></h4>
                        <h4><p style={{ marginBottom: "0" }}>Any questions or to report issues in functionality of this page should be directed to the Local Government team:</p><p style={{marginTop:"0",paddingLeft:"3rem"}}>Phone: (850) 413-5571<br />Email: localgov@myfloridacfo.com</p></h4>
                    </Col>
                </Row>;
            case 'DEW_CALCULATED':
                return <Row>
                    <Col style={{ width: "50%" }}>
                        <DataElementWorksheetCalculated userRole="ADMIN" defaultDewState={dewState} />
                    </Col>
                    <Col>
                        <h4><p style={{ marginBottom: "0" }}>Please direct questions regarding the information required in this page to the Auditor General’s office:</p><p style={{ marginTop: "0", paddingLeft: "3rem" }}>Phone: (850) 487-9301<br />Email: flaudgen_localgovt@aud.state.fl.us</p></h4>
                        <h4><p style={{ marginBottom: "0" }}>Any questions or to report issues in functionality of this page should be directed to the Local Government team:</p><p style={{ marginTop: "0", paddingLeft: "3rem" }}>Phone: (850) 413-5571<br />Email: localgov@myfloridacfo.com</p></h4>
                        <h5><p style={{ marginBottom: "0" }}>The DEW collects information input into the AFR. Financial information is tallied from specific codes, listed below.<br></br>Click on the account name for more information.</p></h5>
                        <Table className="table" >
                            <thead className="thead-dark">
                                <tr>
                                    <th className="col-1">DEW Account</th>
                                    <th className="col-1">Source Account Range</th>
                                </tr>
                            </thead>

                            <tbody>
                                {collectValueMappings(dewState).map((v, idx) =>
                                    <tr key={idx} onClick={() => { loadDewAccount(v.code) }}>
                                        <td style={linkStyle}>{v.name}</td>
                                        <td>{getAccountRange(v)}</td>
                                    </tr>
                                )}

                            </tbody>
                        </Table>
                    </Col>
                </Row>;
            default:
                return null;
        }
    }


    return (
        <React.Fragment>
            <Page>
                <h3>Audit details for {utilityHelper.getEntityName(entity, currentAfr)} ({utilityHelper.getEntityCode(entity, currentAfr)}), {afrYear}</h3>
                {isAdminUser &&
                    (<div className="box-events pl-2 pr-3 py-3 my-2">
                        <div>
                            <Label className="ml-4" check>
                                <Input type="checkbox"
                                    name="reviewState_auditReviewed"
                                    onChange={handleChangeCheckbox}
                                    checked={state["reviewState_auditReviewed"]}
                                    disabled={!utilityHelper.checkStatusList(currentAfr, enums.certifiedRecertified)}
                                /><b>Reviewed</b>
                            </Label>
                        </div>
                    </div>)}
                <h5>
                    <p>Pursuant to Section 218.32(1)(d), Florida Statutes, governments that meet a revenue and expenditure balance threshold are required to submit an independently audited financial statement with their AFR. The completion date of the financial audit would be the date that the CPA firm signs off on the audited financial statements. If an audit is completed, you will be prompted to supply firm and completion information. If an audit is not required, you will be prompted with the Auditor General’s Data Element Worksheet which is required pursuant to Section 218.32(1)(e), Florida Statutes.</p>
                    <p>The Florida Department of Financial Services requires an audit to be submitted and encourages the audit to be submitted electronically and in PDF format. You can upload the audit directly into LOGER by selecting “Upload Audit” below where it can be accessed on the Documents Tab on the AFR Summary Screen, or you can supply the PDF of the audit or a web link to the audit by emailing us at localgov@myfloridacfo.com.</p>
                    <p>To successfully submit the AFR, either the audit information or the Data Element Worksheet must be completed.</p>
                    {auditMessage.requireAudit &&  (utilityHelper.isAuditSatisfied(currentAfr, entity, auditMessage, errorMessage) != "OK" )  &&
                        (<Alert color="danger">
                        <p>This AFR requires an audit.</p>
                        <p><b>Reason: {auditMessage.message }</b></p>
                        </Alert>)}
                </h5>

                {/* The audit is not required */
                  !auditMessage.requireAudit &&
                    auditIsNotRequiredMessage(currentAfr)
                }

                {/* When an audit is not Required then the DEW shoulb be default and the Audit optional */
                    canShowAuditTypeSelector(currentAfr) &&
                    (
                        <div>
                            <Table bordered>
                                <thead>
                                    <tr>
                                        <th className="card-header" style={{
                                            color: "white",
                                            backgroundColor: "#458BCA",
                                            borderRadius: "0px",
                                            fontWeight: "initial"
                                        }}>Was an audit performed?
                                            </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td colSpan={2}>
                                            <Label className="ml-4" check>
                                                <Input type="radio"
                                                    name="radioAudit"
                                                    value={"AUDIT"}
                                                    disabled={utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified)
                                                        || (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin)}
                                                    onChange={handleChange}
                                                    checked={(audit === "AUDIT")}
                                                /><b>Yes</b>
                                            </Label>
                                            {!auditMessage.requireAudit &&  
                                                <Label className="ml-5" check>
                                                    <Input type="radio"
                                                        value={"DEW"}
                                                        name="radioAudit"
                                                        onChange={handleChange}
                                                        checked={(audit === "DEW")}
                                                        disabled={utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified)
                                                            || (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin)}
                                                    /><b>No</b>
                                                </Label>

                                            }
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </div>)}

                {shouldShowAudit(currentAfr, entity) &&
                    <div>            
                        {switchContent(audit)}
                    </div>}

                { !utilityHelper.isDonePrimaryDataEntry(currentAfr, entity) && currentAfr && 
                    (<Alert color="danger">
                    <p>{entityName} must finish entering all the required values. Please ensure the "Done" box is checked on {utilityHelper.getDoneSectionNames(currentAfr, entity)}</p>
                    </Alert>)}


            </Page>
        </React.Fragment>
    );
}

export default AuditCore;